import decode from 'jwt-decode';
import Cookies from 'universal-cookie';

import { logError, moment } from '@feathr/hooks';
import type { IBaseAttributes, TRachisEmpty } from '@feathr/rachis';
import { isWretchError, wretch } from '@feathr/rachis';

interface IToken {
  exp?: number;
  nbf?: number;
  user_id?: string;
  impersonator_id?: string;
  impersonator_name?: string;
}

export interface ILogInResponse extends Record<string, unknown> {
  token: string;
}

export interface IExchangeUrlResponse extends Record<string, unknown> {
  exchange_url: string;
}

enum ETokens {
  userId = 'user_id',
}

export interface IBeeFreeTokenAttributes extends IBaseAttributes {
  access_token: string;
  coediting_session_id: string | null;
  shared: boolean;
  status: 'ok' | 'refreshing';
  v2: boolean;
}

type TValues = keyof typeof ETokens | 'token';

export const SESSION_OTP_CHAR_COUNT = 8;

/**
 * Returns the environment-specific cookie name for the auth token.
 * Each environment (production, staging, qa, development) gets its own cookie name
 * so that logging into one environment does not overwrite the session of another,
 * since they all share the `.feathr.co` domain.
 */
function getTokenCookieName(): string {
  const env = typeof SHRIKE_ENV === 'undefined' ? undefined : SHRIKE_ENV;
  switch (env) {
    case 'staging':
      return 'staging_token';

    case 'qa':
      return 'qa_token';

    case 'development':
      return 'dev_token';

    case 'local':

    case undefined:
      return 'local_token';

    default:
      return 'token';
  }
}

const TOKEN = getTokenCookieName();

export function isTokenValid<T extends { exp?: number; nbf?: number }>(token: string): boolean {
  try {
    const decoded = decode<T>(token);
    const now = moment.utc().unix();
    if (((decoded.nbf && now > decoded.nbf) || !decoded.nbf) && decoded.exp && now < decoded.exp) {
      return true;
    } else {
      return false;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Token is expired.');
    return false;
  }
}

class Session {
  static isLoggingOut = false;

  /**
   * Guards the two endpoints that swap the active session (`beginImpersonation` and `restore`).
   * Both end in a full-page navigation, so a second in-flight call is never useful and is actively
   * harmful: `/session/impersonate` mints a fresh sudo stash and overwrites the handle cookie
   * (orphaning the previous stash until its TTL expires), and `/session/restore` is single-use
   * server-side, so a duplicate call 400s once the first has consumed the stash.
   *
   * The flag stays latched on success — the document is already on its way out, and the fresh page
   * load resets it — and is released only on failure so the user can retry after the error.
   */
  static isSwappingSession = false;

  public static get token(): string {
    const cookies = new Cookies();
    return cookies.get(TOKEN);
  }

  public static set token(token: string) {
    const cookies = new Cookies();
    // The cookie TTL should be synchronized with the expiration of the JWT it stores.
    let expires: Date;
    try {
      const { exp } = decode<IToken>(token);
      expires = exp ? new Date(exp * 1000) : new Date(Date.now() + 24 * 60 * 60 * 1000);
    } catch (error) {
      // Only the error class is reported; the token and the raw error message
      // are bearer-credential material and must never leave the client.
      logError({
        message: 'Auth token could not be decoded; defaulting cookie expiry to 24 hours.',
        level: 'error',
        extra: { errorName: error instanceof Error ? error.name : typeof error },
      });
      expires = new Date(Date.now() + 24 * 60 * 60 * 1000);
    }
    cookies.set(TOKEN, token, { domain: Session.domain, path: '/', expires });
  }

  public static get userId(): string | undefined {
    return Session.get('userId');
  }

  public static get logInUrl(): string {
    const loginUrl = typeof LOGIN_URL === 'undefined' ? 'local.feathr.app:9005' : LOGIN_URL;
    return `//${loginUrl}/`;
  }

  public static get isLoggedIn(): boolean {
    const token = Session.token;
    try {
      return !!token && isTokenValid<IToken>(token);
    } catch (_error) {
      // Token validation failed - treat as not logged in
      return false;
    }
  }

  // Appcue Flow ID cookie helpers
  private static appcueFlowKey = 'appcueFlowId';

  public static setAppcueFlowId(flowId: string): void {
    if (flowId) {
      const cookies = new Cookies();
      // Set cookie for 1 day, domain-wide
      const expires = new Date(Date.now() + 24 * 60 * 60 * 1000);
      cookies.set(Session.appcueFlowKey, flowId, { domain: Session.domain, path: '/', expires });
    }
  }

  public static getAppcueFlowId(): string | null {
    const cookies = new Cookies();
    return cookies.get(Session.appcueFlowKey) || null;
  }

  /**
   * Builds the request headers for an API call.
   *
   * @param useAccountContext - When false, omits the `AccountId` header entirely (account-agnostic
   *   requests).
   * @param accountIdOverride - When provided, scopes the request to this account instead of the
   *   session account. Perch uses this to view an arbitrary account without changing the session
   *   account (which points at the sudoer's own account).
   */
  public static getHeaders(useAccountContext = true, accountIdOverride?: string): HeadersInit {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    const token = Session.token;
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const accountId = accountIdOverride ?? sessionStorage.getItem('accountId');
    if (useAccountContext && accountId) {
      headers.AccountId = accountId;
    }

    return headers;
  }

  public static async logIn(
    email: string,
    password: string,
    otp?: string,
    trust?: boolean,
  ): Promise<void> {
    const response = await wretch<ILogInResponse>(Session.url, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({ email, password, otp, trust }),
      headers: { 'Content-Type': 'application/json' },
    });
    if (isWretchError(response)) {
      throw response.error;
    } else {
      Session.token = response.data.token;
    }
  }

  public static async resendOTP(email: string, password: string): Promise<void> {
    // To re-send, log in without a verification code.
    await Session.logIn(email, password, undefined, undefined);

    if (!Session.isLoggedIn) {
      throw new Error('Your login token is invalid.');
    }
  }

  /**
   * Begin dual-session impersonation: POST to the sudo-gated endpoint, then navigate to the
   * returned `/exchange` URL (landing on `from`). The sudo token is never set in JS — it only
   * materializes in the exchange page — so the caller passes the target landing URL.
   */
  public static async beginImpersonation(
    userId: string,
    accountId: string,
    from: string,
  ): Promise<void> {
    // A session swap is already in flight (a second row's "Login as", or a repeat click that beat
    // the button's pending state). Drop it rather than orphan another sudo stash.
    if (Session.isSwappingSession) {
      return;
    }
    Session.isSwappingSession = true;
    try {
      const response = await wretch<IExchangeUrlResponse>(`${BLACKBOX_URL}session/impersonate`, {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify({ user_id: userId, account_id: accountId }),
        headers: Session.getHeaders(),
      });
      if (isWretchError(response)) {
        throw response.error;
      }
      Session.navigateToExchange(response.data.exchange_url, from);
    } catch (error) {
      Session.isSwappingSession = false;
      throw error;
    }
  }

  /**
   * Return from an impersonated session to the original sudoer session, landing back on the
   * current page.
   */
  public static async restore(): Promise<void> {
    // The stash is single-use: a duplicate call after the first has consumed it 400s.
    if (Session.isSwappingSession) {
      return;
    }
    Session.isSwappingSession = true;
    try {
      const response = await wretch<IExchangeUrlResponse>(`${BLACKBOX_URL}session/restore`, {
        method: 'POST',
        credentials: 'include',
        headers: Session.getHeaders(),
      });
      if (isWretchError(response)) {
        throw response.error;
      }
      Session.navigateToExchange(response.data.exchange_url, window.location.href);
    } catch (error) {
      Session.isSwappingSession = false;
      throw error;
    }
  }

  private static navigateToExchange(exchangeUrl: string, from: string): void {
    const url = new URL(exchangeUrl);
    url.searchParams.set('from', from);
    window.location.assign(url.toString());
  }

  public static get isImpersonating(): boolean {
    const token = Session.token;
    if (!token) {
      return false;
    }
    try {
      return !!decode<IToken>(token).impersonator_id;
    } catch (_error) {
      return false;
    }
  }

  public static get impersonatorName(): string | undefined {
    const token = Session.token;
    if (!token) {
      return undefined;
    }
    try {
      return decode<IToken>(token).impersonator_name;
    } catch (_error) {
      return undefined;
    }
  }

  private static async revokeToken(): Promise<void> {
    if (!Session.isLoggingOut) {
      Session.isLoggingOut = true;
      const url = `${BLACKBOX_URL}logout`;
      const headers = Session.getHeaders();
      const method = 'POST';
      const response = await wretch<TRachisEmpty>(url, {
        headers: headers,
        method: method,
      });
      Session.isLoggingOut = false;
      if (isWretchError(response)) {
        throw response.error;
      }
    }
  }

  public static async getBeeFreeToken(isPage: boolean): Promise<IBeeFreeTokenAttributes> {
    // Page templates and email messages use different client ids/secrets
    const tokenType = isPage ? 'page' : 'message';
    const url = `${BLACKBOX_URL}beefree_token/${tokenType}`;
    const headers = Session.getHeaders();

    // TODO: We need an arg to use the proper id/secret combo for pages vs emails
    const response = await wretch<IBeeFreeTokenAttributes>(url, {
      method: 'GET',
      headers,
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  public static async logOut(callback?: () => void): Promise<void> {
    try {
      // It is imperative that the token is revoked before the cookie is removed.
      await Session.revokeToken();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      throw e;
    } finally {
      try {
        const cookies = new Cookies();
        cookies.remove(TOKEN, { domain: Session.domain, path: '/' });
        cookies.remove(Session.appcueFlowKey, { domain: Session.domain, path: '/' });
        localStorage.clear();
        sessionStorage.clear();
        if (callback) {
          callback();
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        throw e;
      }
    }
  }

  private static url = `${BLACKBOX_URL}jwt`;
  private static domain = process.env.NODE_ENV === 'production' ? '.feathr.co' : 'local.feathr.app';

  private static get(key: TValues, defaultValue?: string): string | undefined {
    const token = Session.token;
    if (key === 'token') {
      return token ? token : defaultValue;
    }

    if (!token) {
      return defaultValue;
    }

    const decoded = decode<IToken>(token);
    const translatedKey = ETokens[key];
    return decoded[translatedKey] !== undefined ? decoded[translatedKey] : defaultValue;
  }
}

export default Session;
